// The reorderings the user has dragged into place, above the trace.
//
// Each entry is a stored `Reorder` (a pair of step ids) resolved against the
// run currently on screen, so it reads as the sentence the drag meant —
// "T2 read /account/1 before T1 write /account/1" — and can be removed. An
// entry whose steps are gone (an earlier reordering changed that thread's
// history) shows as stale rather than silently disappearing.

import { useEffect, useRef } from "react";
import type { RunResult } from "../../src/backend/interp/interpreter.js";
import type { Reorder, ReorderStatus } from "../reorder.js";
import { eventColumns } from "../trace-layout.js";
import type { ObjectTypes } from "../trace-layout.js";

interface ReorderStripProps {
  result: RunResult;
  reorders: readonly Reorder[];
  statuses: readonly ReorderStatus[];
  error?: { index: number; message: string };
  types?: ObjectTypes;
  onRemove(index: number): void;
  onClear(): void;
}

/** A step in the run on screen, as "T1 write /account/1" — its thread plus the
 *  event that best names it. Null when that step is no longer in the run.
 *
 *  A step is one statement's worth of work, and `acct.balance = next` emits a
 *  read (fetching the object to rebuild it) before its write. That read is noise
 *  next to the write, so the headline is the step's first *non*-read event —
 *  falling back to the first event for a step that only reads. */
function describeStep(result: RunResult, id: string, types?: ObjectTypes): string | null {
  const c = result.causality;
  const step = c?.steps.find((s) => s.id === id);
  if (!step) return null;
  const evs = result.trace.events.slice(step.firstEvent, step.endEvent);
  const ev = evs.find((e) => e.type !== "read") ?? evs[0];
  if (!ev) return `T${step.thread}`;
  const { op, key } = eventColumns(ev, types); // `key` is already type-qualified
  return `T${step.thread} ${op}${key ? ` ${key}` : ""}`;
}

export function ReorderStrip({
  result,
  reorders,
  statuses,
  error,
  types,
  onRemove,
  onClear,
}: ReorderStripProps) {
  // The chip a drag is writing is the last one, and with a few already there the
  // rail has scrolled past it. Keep the end in view whenever the list changes,
  // so the sentence the gesture is composing is the one you can read. (Declared
  // before the early return below: hooks may not be conditional.)
  const rail = useRef<HTMLDivElement>(null);
  const last = reorders[reorders.length - 1];
  useEffect(() => {
    if (rail.current) rail.current.scrollLeft = rail.current.scrollWidth;
  }, [reorders.length, last?.moved, last?.target, last?.side]);

  // With nothing reordered, the strip is the affordance instead: it says which
  // interleaving you're looking at, and that you can change it.
  if (reorders.length === 0) {
    return (
      <div id="reorders">
        <span className="reorder-title">default order</span>
        <div className="reorder-rail" ref={rail}>
          <span className="reorder-hint">
            drag a row up or down to run the program under that interleaving instead
          </span>
        </div>
      </div>
    );
  }
  // Chips and any error live in a rail that scrolls sideways, so the strip's
  // height never changes: it sits directly above the trace, and a chip appears
  // *mid-drag* — a strip that grew would push the rows the user is holding out
  // from under the pointer. See the CSS note.
  return (
    <div id="reorders">
      <span className="reorder-title" title="the scheduler's tie-break, overruled by hand">
        reordered
      </span>
      <div className="reorder-rail" ref={rail}>
      {reorders.map((r, i) => {
        const status = statuses[i] ?? "stale";
        const moved = describeStep(result, r.moved, types);
        const target = describeStep(result, r.target, types);
        const label =
          moved && target ? `${moved} ${r.side} ${target}` : "step no longer in this run";
        return (
          <span key={i} className={`reorder-chip ${status}`} title={TITLES[status]}>
            <b>{label}</b>
            <button onClick={() => onRemove(i)} title="remove this reordering">
              ✕
            </button>
          </span>
        );
      })}
      {error && (
        <span
          className="reorder-error"
          title={`reordering #${error.index + 1} broke the run: ${error.message}`}
        >
          reordering #{error.index + 1} broke the run: {error.message}
        </span>
      )}
      </div>
      <button className="reorder-reset" onClick={onClear}>
        reset to the default order
      </button>
    </div>
  );
}

const TITLES: Record<ReorderStatus, string> = {
  applied: "realized: the run below has these steps in this order",
  stale: "one of these steps is no longer in the run — an earlier reordering changed it",
  unrealized: "the steps are still here, but the scheduler couldn't be held to this order",
};
